import { supabase } from '../supabaseClient';

export interface Review {
  id: string;
  project_id: string;
  provider_id: string;
  client_id: string;
  rating_quality: number;
  rating_communication: number;
  rating_deadline: number;
  comment?: string;
  created_at: string;
  updated_at: string;
}

export interface ReviewRequest {
  project_id: string;
  provider_id: string;
  rating_quality: number;
  rating_communication: number;
  rating_deadline: number;
  comment?: string;
}

export interface ProjectAccessProvider {
  provider_id: string;
  provider_name: string;
  provider_avatar: string;
  access_purchased_at: string;
}

// Get providers who purchased access to a project
export const getProjectAccessProviders = async (
  projectId: string
): Promise<{ data: ProjectAccessProvider[] | null; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('get_project_access_providers', {
      p_project_id: projectId,
      p_client_id: user.id
    });

    if (error) {
      console.error('Error fetching project access providers:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching project access providers:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Check if user can leave review for project
export const canLeaveReview = async (
  projectId: string,
  providerId: string
): Promise<{ canReview: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { canReview: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('can_leave_review', {
      p_project_id: projectId,
      p_provider_id: providerId,
      p_client_id: user.id
    });

    if (error) {
      console.error('Error checking review eligibility:', error);
      return { canReview: false, error: error.message };
    }

    return { canReview: data, error: undefined };
  } catch (error) {
    console.error('Error checking review eligibility:', error);
    return { 
      canReview: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Create a review
export const createReview = async (
  reviewData: ReviewRequest
): Promise<{ data: Review | null; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    // Check if user can leave review
    const { canReview, error: checkError } = await canLeaveReview(
      reviewData.project_id,
      reviewData.provider_id
    );

    if (checkError) {
      return { data: null, error: checkError };
    }

    if (!canReview) {
      return { 
        data: null, 
        error: 'You cannot leave a review for this provider. They must have purchased access to your project.' 
      };
    }

    const { data, error } = await supabase
      .from('reviews')
      .insert({
        project_id: reviewData.project_id,
        provider_id: reviewData.provider_id,
        client_id: user.id,
        rating_quality: reviewData.rating_quality,
        rating_communication: reviewData.rating_communication,
        rating_deadline: reviewData.rating_deadline,
        comment: reviewData.comment
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating review:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error creating review:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Get reviews for a provider
export const getProviderReviews = async (
  providerId: string,
  limit: number = 10,
  offset: number = 0
): Promise<{ data: Review[] | null; error: string | null; count: number }> => {
  try {
    const { data, error, count } = await supabase
      .from('reviews')
      .select(`
        *,
        client:profiles!reviews_client_id_fkey(
          id,
          full_name,
          avatar_url,
          company
        )
      `, { count: 'exact' })
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching provider reviews:', error);
      return { data: null, error: error.message, count: 0 };
    }

    return { data, error: null, count: count || 0 };
  } catch (error) {
    console.error('Error fetching provider reviews:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error',
      count: 0
    };
  }
};

// Get reviews for a project
export const getProjectReviews = async (
  projectId: string
): Promise<{ data: Review[] | null; error: string | null }> => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        *,
        provider:profiles!reviews_provider_id_fkey(
          id,
          full_name,
          avatar_url,
          company
        )
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching project reviews:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching project reviews:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Update a review
export const updateReview = async (
  reviewId: string,
  reviewData: Partial<ReviewRequest>
): Promise<{ data: Review | null; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('reviews')
      .update({
        rating_quality: reviewData.rating_quality,
        rating_communication: reviewData.rating_communication,
        rating_deadline: reviewData.rating_deadline,
        comment: reviewData.comment,
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId)
      .eq('client_id', user.id) // Ensure user owns the review
      .select()
      .single();

    if (error) {
      console.error('Error updating review:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating review:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Delete a review
export const deleteReview = async (
  reviewId: string
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId)
      .eq('client_id', user.id); // Ensure user owns the review

    if (error) {
      console.error('Error deleting review:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting review:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Complete project and show review options
export const completeProject = async (
  projectId: string
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('complete_project', {
      p_project_id: projectId,
      p_client_id: user.id
    });

    if (error) {
      console.error('Error completing project:', error);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: 'You do not own this project or project not found' };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error completing project:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};
